/**
 * MerchantAccountSection.tsx - Payment Merchant Account Configuration
 *
 * This component manages merchant account selection and configuration for
 * payment processing with account validation and setup options.
 *
 * Key Features:
 * - Merchant account selection from available accounts
 * - Account configuration and validation
 * - Payment processor integration settings
 * - Account status and health monitoring
 * - Fee structure and processing information
 * - Test mode and production settings
 *
 * Account Types:
 * - Main Account: Primary organizational payment account
 * - Alternate Account: Secondary account for specific programs
 * - Test Account: Development and testing payment processing
 * - Custom Account: User-configured payment processors
 *
 * Configuration Options:
 * - Account Selection: Choose from configured accounts
 * - Processing Settings: Configure payment processing options
 * - Fee Management: Display and configure processing fees
 * - Currency Settings: Set accepted currencies
 * - Geographic Restrictions: Configure payment regions
 *
 * Validation Features:
 * - Account connectivity testing
 * - Configuration validation
 * - Processing capability verification
 * - Error handling and troubleshooting
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: Payment processor APIs, account validation, MobX store
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../../../stores";
import {
  ApplicationStatus,
  MerchantAccount,
} from "@mindmemobile/mm-payment-modules";
import { CreateFundraiserModel } from "../../../models";
import Select, { SingleValueProps } from "react-select";
import { useOutsideClick } from "@mindmemobile/mm-shared-global";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
  .merchant-account-select {
    width: 50%;
    .merchant-select__control {
      position: relative;
      padding: 16px 20px;
      border: 2px solid rgb(59, 130, 246);
      border-radius: 12px;
      background: rgb(240, 249, 255);
      cursor: pointer;
      transition: 0.2s;
      margin-bottom: 16px;
      width: 100%;
    }
    .merchant-select__menu {
      z-index: 99999;
      background: rgb(255, 255, 255);
      border: 2px solid rgb(226, 232, 240);
      border-radius: 12px;
      box-shadow:
        rgba(0, 0, 0, 0.1) 0px 10px 25px -5px,
        rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
      /* max-height: 200px; */
      overflow-y: auto;
      margin-top: 4px;
      display: block;
    }
    .merchant-select__menu-list {
      width: 100%;
      max-height: 213px;

      > div {
        padding: 10px 20px !important;
        cursor: pointer;
        span {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          cursor: pointer;
        }
        &:hover {
          background-color: #f0f9ffa1;
          cursor: pointer;
        }
      }
      img {
        width: 72px !important;
        height: auto !important;
        object-fit: contain !important;
      }
    }
    .merchant-select__input-container {
      display: none !important;
    }
    .merchant-select__indicator-separator {
      display: none !important;
    }
    .merchant-select__value-container {
      > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        span {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
        }
        img {
          width: 72px !important;
          height: auto !important;
          object-fit: contain !important;
        }
      }
      .merchant-select__placeholder {
        font-size: 18px;
        font-weight: 500;
      }
    }
    &.merchant-error-border {
      .merchant-select__control {
        border: 1px solid red !important;
      }
    }
  }
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const AccountCard = styled.div<{ selected: boolean; error?: boolean }>`
  position: relative;
  padding: 16px 20px;
  border: 2px solid
    ${(props) =>
    props.error
      ? "#ef4444" /* red for error */
      : props.selected
        ? "#3b82f6" /* blue when selected */
        : "#e2e8f0" /* gray default */
  };
  border-radius: 12px;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  width: 50%;
  display: flex;
  justify-content: space-between;

  &:hover {
    border-color: ${(props) =>
    props.error ? "#ef4444" : "#3b82f6"}; /* keep red if error */
    background: #f8fafc;
  }
`;

const AccountHeader = styled.div`
  position: relative;
  /* height: 24px; */
  /* margin-bottom: 8px; */
  display: flex;
  align-items: center;
`;

const AccountName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`;

const AccountDetails = styled.div`
  color: #64748b;
  font-size: 14px;
  line-height: 1.4;
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

const merchantAccounts = [
  {
    id: "main",
    name: "Main Charity",
    description: "Primary organizational account for general donations",
    type: "primary",
    color: "#10b981",
    logo: "/fiserv-logo.png",
  },
  {
    id: "alternate",
    name: "Alternate Charity",
    description: "Secondary account for specific programs and initiatives",
    type: "secondary",
    color: "#3b82f6",
    logo: "/tsys-logo.png",
  },
];

interface MerchantAccountSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
}

interface MerchantDropDownOptions {
  value: string;
  label: string;
  logo: string;
}

export const MerchantAccountSection: React.FC<MerchantAccountSectionProps> =
  observer(({ builderData, updateBuilderField }) => {
    const [selectedMerchantAccount, setSelectedMerchantAccount] = useState(
      builderData.merchantAccount
    );
    const [open, setOpen] = useState(false);

    const handleMerchantAccountSelect = (account: MerchantAccount) => {
      setSelectedMerchantAccount(account);
      updateBuilderField("merchantAccount", account);
    };
    const merchantAccounts = fundraiserStore.merchantAccounts.filter(
      (m) => m.applicationStatus === ApplicationStatus.Active
    );

    useEffect(() => {
      setSelectedMerchantAccount(builderData.merchantAccount);
    }, [builderData.merchantAccount]);

    // Your options with name + logo
    const options: MerchantDropDownOptions[] = merchantAccounts.map(
      (account) => ({
        value: account.id,
        label: account.name,
        logo: account.getLogo(),
      })
    );

    // Custom Option component
    const CustomOption = (props: SingleValueProps<MerchantDropDownOptions>) => {
      return (
        <div
          {...props.innerProps}
          style={{ display: "flex", alignItems: "center", padding: "5px" }}
        >
          <span style={{ flex: 1 }}>{props.data.label}</span>
          <img
            src={props.data.logo}
            alt={props.data.label}
            style={{ width: 20, height: 20, marginLeft: 8 }}
          />
        </div>
      );
    };

    // Custom SingleValue (selected value display)
    const CustomSingleValue = (
      props: SingleValueProps<MerchantDropDownOptions>
    ) => (
      <div style={{ display: "flex", alignItems: "center" }}
        onMouseDown={(e) => {
          e.stopPropagation();
          if (props.selectProps.menuIsOpen) {
            props.selectProps.onMenuClose();
          } else {
            props.selectProps.onMenuOpen();
          }
        }}>
        <span>{props.data.label}</span>
        {/* dgdf */}
        <img
          src={props.data.logo}
          alt={props.data.label}
          style={{ width: 20, height: 20, marginLeft: 8 }}
        />
      </div>
    );
    const defaultOption = options.find(
      (opt) => opt.value === selectedMerchantAccount.id
    );

    const showDropdown = merchantAccounts.length > 3;

    const ref = useOutsideClick(() => setOpen(false));
    return (
      <Section>
        <SectionTitle>Merchant Account</SectionTitle>

        <FormGroup >
          {showDropdown && (
            <div ref={ref}>
              <Select

                menuIsOpen={open}
                onMenuOpen={() => setOpen(true)}
                onMenuClose={() => setOpen(false)}

                className={`merchant-account-select ${builderData.hasError("merchantAccount") ? "merchant-error-border" : ""} `}
                options={options}
                value={defaultOption}
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue,
                }}
                onChange={(newValue: any) => {
                  const account = merchantAccounts.find(
                    (acc) => acc.id === newValue.value
                  );
                  account && handleMerchantAccountSelect(account);
                }}
                placeholder="Select Merchant Account"
                classNamePrefix="merchant-select"
              // menuIsOpen={true}
              />
            </div>
          )}
          {!showDropdown &&
            merchantAccounts.map((account) => (
              <AccountCard
                key={account.id}
                selected={selectedMerchantAccount.id === account.id}
                onClick={() => handleMerchantAccountSelect(account)}
                error={builderData.hasError("merchantAccount")}
              >
                <AccountHeader>
                  <AccountName>{account.name}</AccountName>
                </AccountHeader>
                {/* Logo positioned relative to the entire card, not just the header */}
                <div
                  style={{
                    // position: "absolute",
                    // right: "20px",
                    // top: "50%",
                    // transform: "translateY(-50%)",
                    width: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={account.getLogo()}
                    alt={`${account.name} logo`}
                    style={{
                      width: "72px",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>
                {/* <AccountDetails> {account.description}</AccountDetails> */}
              </AccountCard>
            ))}
          <HelpText>
            Select which merchant account will process the payments for this
            fundraiser. This determines where the funds will be deposited.
          </HelpText>
        </FormGroup>
      </Section>
    );
  });
